import { logout } from 'actions/entities/user/user';
import { newNotification } from 'actions/ui/ui';
import { ToggleMenu } from 'components/ToggleMenu/ToggleMenu';
import {
  CASHBACK_ROUTE,
  LOGOUT_ROUTE,
  PAYMENTS_ROUTE,
  PROFILE_ROUTE,
} from 'constants/routes';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from 'reducers/types';
import { getAccessToken } from 'selectors/entities/authentication/authentication';
import { getUser } from 'selectors/entities/user/user';
import { useTranslations } from 'utils/translations/useTranslations';

import './MyAccountMenu.scss';
import { FF_REWARDS_CHINA } from 'constants/features';
import { usePeertransferFlags } from 'hooks/usePeertransferFlags/usePeertransferFlags';
import { getRewardsCurrency, paymentsFromIndia } from 'selectors/ui';

type MyAccountMenuProps = ConnectedProps<typeof connector> & {
  onClick?: () => void;
};

export function MyAccountMenuComponent({
  accessToken,
  paymentsFromIndia = false,
  rewardsCurrency = null,
  logout,
  newNotification,
  onClick,
  user,
}: MyAccountMenuProps) {
  const navigate = useNavigate();
  const i18n = useTranslations();
  const { data: featureFlags } = usePeertransferFlags([FF_REWARDS_CHINA]);

  if (!accessToken) {
    return null;
  }

  const logoutAndNotify = () => {
    logout();
    newNotification({ message: 'notifications.logout_user_success' });
    onClick?.();
  };

  const goTo = (path: string) => {
    navigate(path);
    onClick?.();
  };

  const { firstName: userName } = user;

  const menuItems = [
    {
      label: i18n.t('myPayments'),
      path: PAYMENTS_ROUTE,
      onClick: () => goTo(PAYMENTS_ROUTE),
    },
    {
      label: i18n.t('profile'),
      path: PROFILE_ROUTE,
      onClick: () => goTo(PROFILE_ROUTE),
    },
    {
      className: 'is-logout',
      label: i18n.t('logout'),
      path: LOGOUT_ROUTE,
      onClick: () => logoutAndNotify(),
    },
  ];

  const isRewardChina =
    featureFlags?.[FF_REWARDS_CHINA as keyof typeof featureFlags];

  const hasValidRewardsForChina = isRewardChina && !!rewardsCurrency;
  const hasValidRewardsForIndia = !isRewardChina && paymentsFromIndia;

  const rewardsEnabled = hasValidRewardsForChina || hasValidRewardsForIndia;

  if (rewardsEnabled) {
    menuItems.splice(1, 0, {
      className: 'hidden md:block',
      label: i18n.t('myCashBack'),
      path: CASHBACK_ROUTE,
      onClick: () => goTo(CASHBACK_ROUTE),
    });
  }

  return (
    <>
      {userName && (
        <span
          className="MyAccountMenu-welcomeMessage"
          data-testid="welcomeUser"
        >
          {i18n.t('myAccountMenu.welcomeMessage', { userName })}
        </span>
      )}
      <ToggleMenu menuItems={menuItems} label={i18n.t('myAccount')} />
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  accessToken: getAccessToken(state),
  user: getUser(state),
  paymentsFromIndia: paymentsFromIndia(state),
  rewardsCurrency: getRewardsCurrency(state),
});

const mapDispatchToProps = {
  logout,
  newNotification,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const MyAccountMenu = connector(MyAccountMenuComponent);

export { MyAccountMenu };
